import { ExternalLink } from "lucide-react";
import { formatToCurrency } from "@utils/formatting";
import { PlainTextContent } from "@shared-kernel/primary/shared/plain-text-content/plain-text-content.content";
import { TrainingSchedulesDisplay } from "@shared-kernel/primary/shared/training-schedules-display/training-schedules-display.content";
import { AttachmentsDisplay } from "./attachments-display.content";
import { VariantCard } from "./variant-card.content";
import type { TrainingDisplayData } from "./types";

interface TrainingDisplayCardProps {
  training: TrainingDisplayData;
  variant: "admin" | "provider";
}

const DEFAULT_TITLES = {
  admin: "Masterclass",
  provider: "Détails de la formation",
} as const;

/**
 * Shared component to display training information including title, description,
 * objectives, price, schedules, and attachments.
 * Uses variant to control layout: admin has title inside card, provider has title outside.
 */
export const TrainingDisplayCard = ({ training, variant }: TrainingDisplayCardProps) => {
  const title = DEFAULT_TITLES[variant];
  const hasAttachments = training.attachments && training.attachments.length > 0;
  const hasSchedules = training.trainingSchedules && training.trainingSchedules.length > 0;

  return (
    <VariantCard title={title} variant={variant} contentClassName="space-y-4">
      {/* Training title */}
      <div className="text-sm">
        <span className="text-muted-foreground">Titre :</span> <span className="font-medium">{training.title}</span>
      </div>

      {/* Description */}
      {training.description && (
        <div className="space-y-1">
          <span className="text-sm text-muted-foreground">Description</span>
          <div className="max-h-32 overflow-auto rounded-md border border-muted-foreground/20 p-2 text-sm">
            <PlainTextContent content={training.description} />
          </div>
        </div>
      )}

      {/* Objective */}
      {training.objective && (
        <div className="space-y-1">
          <span className="text-sm text-muted-foreground">Objectifs</span>
          <div className="max-h-32 overflow-auto rounded-md border border-muted-foreground/20 p-2 text-sm">
            <PlainTextContent content={training.objective} />
          </div>
        </div>
      )}

      {/* Price */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Prix :</span>
        <span className="font-semibold">{formatToCurrency(training.price)}</span>
      </div>

      {/* Website URL */}
      {training.websiteUrl && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Site web :</span>
          <a
            href={training.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 break-all text-primary hover:underline"
          >
            <span className="min-w-0">{training.websiteUrl}</span>
            <ExternalLink className="size-3 shrink-0" />
          </a>
        </div>
      )}

      {/* Attachments */}
      {hasAttachments && <AttachmentsDisplay attachments={training.attachments} variant={variant} withTopBorder />}

      {/* Training Schedules */}
      {hasSchedules && (
        <TrainingSchedulesDisplay schedules={training.trainingSchedules} className="border-t border-muted-foreground/20 pt-4" />
      )}
    </VariantCard>
  );
};
